import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatUnifiedPrice } from '../../utils/priceCalculatorUnified';
import { PRICING_CONFIG } from '../../config/pricingConfig';
import './StripePaymentModal.css';

const ProductCashConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  reservationData,
  breakdown,
  paymentMethod,
  loading = false,
}) => {
  const { t } = useTranslation(['common', 'products', 'booking', 'listing']);
  const [priceDetailsOpen, setPriceDetailsOpen] = useState(true);

  if (!open || !reservationData) return null;

  const paymentLabel = paymentMethod === 'bank_transfer'
    ? t('products:order.payment.transfer', 'Virement')
    : t('products:order.payment.cash', 'Espèces');

  const displayedClientTotal = breakdown?.client_total || ((breakdown?.rental_amount || 0) + (breakdown?.service_fee || 0) + (breakdown?.tax_amount || 0));
  const displayedSubtotal = (breakdown?.pricing_display_mode === 'included')
    ? displayedClientTotal
    : ((breakdown?.rental_amount || 0) + (breakdown?.service_fee || 0));
  const netPrice = displayedClientTotal - (breakdown?.service_fee || 0) - (breakdown?.tax_amount || 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white'
        }}>
          <h2 style={{ color: 'white' }}>💵 {t('products:order.cash.modalTitle', 'Confirmer la commande')}</h2>
          <button
            className="close-button"
            onClick={onClose}
            type="button"
            aria-label={t('common:close', 'Fermer')}
            style={{ color: 'white' }}
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="stripe-payment-form">
            <div className="payment-summary">
              <div
                className="payment-summary-header"
                onClick={() => setPriceDetailsOpen(!priceDetailsOpen)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'nowrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                  <span style={{ fontSize: '1.2rem' }}>💵</span>
                  <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>
                    {t('products:order.cash.summaryTitle', 'Récapitulatif du paiement')}
                  </h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 120, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1e293b', marginLeft: '12px' }}>
                    {formatUnifiedPrice(displayedClientTotal, breakdown?.currency || 'EUR')}
                  </span>
                  <span style={{ fontSize: '1.2rem', transform: priceDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                    ▼
                  </span>
                </div>
              </div>

              <div className={`breakdown ${priceDetailsOpen ? 'open' : 'closed'}`}>
                {/* Dates Header */}
                {reservationData?.start_date && (
                  <div className="line-item" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600 }}>{t('products:slots.date', 'Dates')}</span>
                    <span style={{ textAlign: 'right' }}>
                      {(() => {
                        try {
                          const s = reservationData.start_date.substring(0, 10);
                          const sd = parseISO(s);
                          return isValid(sd)
                            ? format(sd, 'dd/MM/yyyy', { locale: fr })
                            : reservationData.start_date.substring(0, 10);
                        } catch {
                          return reservationData.start_date.substring(0, 10);
                        }
                      })()}
                    </span>
                  </div>
                )}

                {/* Commande avec horaires */}
                {reservationData?.start_at && reservationData?.end_at && (
                  <div className="line-item" style={{ color: '#6200ee', fontSize: '0.9em', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600 }}> 📦 ({reservationData.start_at.substring(0, 10)} {reservationData.start_at.substring(11, 16)} → {reservationData.end_at.substring(11, 16)})</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatUnifiedPrice(breakdown?.rental_amount || 0, breakdown?.currency || 'EUR')}
                    </span>
                  </div>
                )}

                {/* Quantité */}
                <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
                  <span>{t('booking:sidebar.form.quantity', 'Quantité')}</span>
                  {(() => {
                    const qty = reservationData?.quantity_requested || 1;
                    return (
                      <span>{qty} {t('booking:sidebar.form.unit', { count: qty })}</span>
                    );
                  })()}
                </div>

                {/* Participants */}
                {reservationData?.guest_count != null && (
                  <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
                    <span>{t('booking:sidebar.form.guestCount.label', 'Participants')}</span>
                    <span>{reservationData.guest_count}</span>
                  </div>
                )}

                {/* Places à réserver */}
                {reservationData?.seats_requested != null && (
                  <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
                    <span>{t('booking:sidebar.form.seatsLabel', 'Places à réserver')}</span>
                    <span>{reservationData.seats_requested}</span>
                  </div>
                )}

                <div className="line-item">
                  <span>{t('products:order.cart.netPrice','Prix net')}</span>
                  <span>{formatUnifiedPrice(netPrice, breakdown?.currency || 'EUR')}</span>
                </div>

                {/* Ligne livraison ou retrait */}
                {(() => {
                  const extraFees = reservationData?.extra_fees;
                  const isDelivery = extraFees?.delivery_fee_cents != null;
                  const deliveryFeeCents = extraFees?.delivery_fee_cents || 0;
                  const deliveryType = extraFees?.delivery_type || 'standard';
                  
                  if (isDelivery) {
                    // Mode livraison
                    return (
                      <div className="line-item" style={{ color: '#6200ee', fontSize: '0.95em' }}>
                        <span>
                          {deliveryType === 'express' ? '⚡ ' : '🚚 '}
                          {deliveryType === 'express' 
                            ? t('products:order.deliveryFee.express', 'Livraison express')
                            : t('products:order.deliveryFee.standard', 'Livraison standard')}
                        </span>
                        <span>
                          {deliveryFeeCents > 0 
                            ? formatUnifiedPrice(deliveryFeeCents / 100, breakdown?.currency || 'EUR')
                            : t('products:order.deliveryFee.free', 'GRATUITE')}
                        </span>
                      </div>
                    );
                  } else {
                    // Mode retrait sur place
                    return (
                      <div className="line-item" style={{ color: '#666', fontSize: '0.9em' }}>
                        <span>🏪 {t('products:order.pickup.free', 'Retrait sur place')}</span>
                        <span>{t('products:order.deliveryFee.free', 'Gratuit')}</span>
                      </div>
                    );
                  }
                })()}

                <div className="line-item">
                  <span>{t('common:serviceFee')} ({breakdown?.commission_rate_percent ?? PRICING_CONFIG.SERVICE_FEE_PERCENT}%)</span>
                  <span>{formatUnifiedPrice(breakdown?.service_fee || 0, breakdown?.currency || 'EUR')}</span>
                </div>

                {/* TVA - Styling amélioré */}
                {(breakdown?.tax_amount > 0) && (
                  <div className="line-item" style={{ color: '#888', fontSize: '0.9em' }}>
                    {(() => {
                      const vr = breakdown?.vat_rate_percent ?? PRICING_CONFIG.TAX_PERCENT;
                      return <span>{t('sidebar.pricing.tax', { percent: vr, defaultValue: `TVA (${vr}%)` })}</span>;
                    })()}
                    <span>{formatUnifiedPrice(breakdown.tax_amount, breakdown?.currency || 'EUR')}</span>
                  </div>
                )}

                

                <div className="line-item total">
                  <span><strong>{t('products:order.cart.total', 'Total')}</strong></span>
                  <span><strong>{formatUnifiedPrice(displayedClientTotal, breakdown?.currency || 'EUR')}</strong></span>
                </div>

                {/* Lieu */}
                {reservationData?.pickup_address && (
                  <div style={{ 
                    marginTop: '10px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid #e2e8f0', 
                    fontSize: '0.85em', 
                    color: '#555' 
                  }}>
                    <span style={{ fontWeight: 600 }}>📍 {t('booking:sidebar.payment.addresses', 'Adresses')} :</span>{' '}
                    <span>{reservationData.pickup_address}</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ margin: '16px 0', padding: '14px 16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1e293b' }}>
                {paymentLabel}
              </div>
              <div style={{ fontSize: '0.88em', color: '#666' }}>
                {paymentMethod === 'bank_transfer'
                  ? t('products:order.payment.transferDesc', 'Virement bancaire avant le pickup.')
                  : t('products:order.payment.cashDesc', 'Remise en main propre lors du pickup.')}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('booking:payment.stripe.buttons.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    {t('booking:payment.stripe.processing', 'Traitement en cours...')}
                  </>
                ) : (
                  <>
                    {t('products:order.cash.confirmButton', 'Confirmer la commande')}
                  </>
                )}
              </button>
            </div>

            <div className="security-info">
              <div className="security-badge">
                {t('products:order.cash.secureInfo', 'Commande sécurisée Krillou')}
              </div>
              <p className="security-text">
                {t('products:order.cash.securityText', 'Votre commande est protégée par notre politique de paiement et de remise en main propre.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCashConfirmationModal;